/**
 * providers/ProviderRegistry.ts — the plug-and-play switchboard.
 *
 * The engine NEVER imports a concrete provider; it asks the registry for a
 * capability. Swapping mock → real is one `register()` call:
 *
 *   providerRegistry.register('places', new GooglePlacesProvider(...));
 *
 * Placeholder providers are pre-registered so the engine always works offline.
 */

import type { ProviderCapability, ProviderInterfaces, ProviderMeta } from './types';
import {
  mockBookingProvider,
  mockCityMetricsProvider,
  mockCommunityReviewProvider,
  mockExchangeRateProvider,
  mockHousingProvider,
  mockInternetQualityProvider,
  mockPlacesProvider,
  mockTravelAdvisoryProvider,
  mockWeatherProvider,
} from './mockProviders';

export class ProviderRegistry {
  private providers = new Map<ProviderCapability, ProviderInterfaces[ProviderCapability]>();

  register<C extends ProviderCapability>(capability: C, provider: ProviderInterfaces[C]): void {
    this.providers.set(capability, provider);
  }

  get<C extends ProviderCapability>(capability: C): ProviderInterfaces[C] {
    const provider = this.providers.get(capability);
    if (!provider) {
      // Explicit failure — never silently fall back mid-flight. Defaults are
      // registered at construction, so this only fires if someone unregisters.
      throw new Error(`[RoamerIntelligence] No provider registered for capability "${capability}"`);
    }
    return provider as ProviderInterfaces[C];
  }

  has(capability: ProviderCapability): boolean {
    return this.providers.has(capability);
  }

  /** Provenance overview — which capabilities are live vs placeholder. */
  listProviders(): ProviderMeta[] {
    return Array.from(this.providers.values()).map((p) => ({ ...p.meta }));
  }
}

/** Build a registry with every capability backed by its placeholder provider. */
export function createDefaultRegistry(): ProviderRegistry {
  const registry = new ProviderRegistry();
  registry.register('places', mockPlacesProvider);
  registry.register('housing', mockHousingProvider);
  registry.register('booking', mockBookingProvider);
  registry.register('weather', mockWeatherProvider);
  registry.register('exchangeRate', mockExchangeRateProvider);
  registry.register('internetQuality', mockInternetQualityProvider);
  registry.register('travelAdvisory', mockTravelAdvisoryProvider);
  registry.register('communityReviews', mockCommunityReviewProvider);
  registry.register('cityMetrics', mockCityMetricsProvider);
  return registry;
}

/** App-wide default registry. Real integrations register themselves here. */
export const providerRegistry = createDefaultRegistry();
